/**
 * 📦 ADMIN PRODUCTS APPROVAL PAGE - REFATORADO
 * 
 * Padrão seguindo a tela de Tickets: botões de filtro, busca separada, StatCard, Table
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import StatCard from '../../components/common/StatCard';
import AdminPageHeader from '../../components/common/AdminPageHeader';
import ApprovalFilters from '../../components/admin/ApprovalFilters';
import { Drawer } from '../../theme/components';
import { selectUser } from '../../services/reducers/user';
import CurrencyFormatter from '../../utils/CurrencyFormatter';
import { Card, Row, Col, Modal, Button, Form, Alert, Badge, Spinner, InputGroup, Table } from 'react-bootstrap';
import { API_URL } from '../../config/api';
import styles from './AdminProductsApprovalPage.module.scss';

const AdminProductsApprovalPage = () => {
  const user = useSelector(selectUser);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado de abas
  const [activeView, setActiveView] = useState('pending');
  
  // Filtros e paginação
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });

  // Estados para Drawer e Modais
  const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');
  const [rejectionReason, setRejectionReason] = useState('');

  // Mapear activeView para status da API
  const getStatusFilter = () => {
    switch (activeView) {
      case 'pending':
        return 'pending_approval';
      case 'approved':
        return 'approved';
      case 'rejected':
        return 'rejected';
      default:
        return 'all';
    }
  };

  // Carregar produtos
  useEffect(() => {
    loadProducts();
    loadStats();
  }, [filters, pagination.page, activeView]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      setError(null);

      const statusFilter = getStatusFilter();
      const token = localStorage.getItem('auth_token');
      const queryParams = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        status: statusFilter,
        ...(filters.date_from && { date_from: filters.date_from }),
        ...(filters.date_to && { date_to: filters.date_to })
      });

      const response = await fetch(`${API_URL}/api/admin/products/pending?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar produtos');
      }

      const data = await response.json();

      if (data.success) {
        setProducts(data.data.products || []);
        setPagination(prev => ({
          ...prev,
          total: data.data.pagination.total,
          totalPages: data.data.pagination.totalPages,
          hasNext: data.data.pagination.hasNext,
          hasPrev: data.data.pagination.hasPrev
        }));
        setStats(data.data.stats);
      } else {
        throw new Error(data.message || 'Erro ao carregar produtos');
      }
      
    } catch (err) {
      setError(err.message);
      console.error('Erro ao carregar produtos:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/products/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.data);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar estatísticas:', err);
    }
  };

  // Carregar detalhes completos do produto
  const loadProductDetails = async (productId) => {
    try {
      setLoadingDetails(true);
      setError(null);
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('Token de autenticação não encontrado');
      }
      
      console.log('🔍 Carregando detalhes do produto:', productId);
      console.log('🔍 URL:', `${API_URL}/api/admin/products/${productId}`);
      
      // Buscar dados completos do produto via endpoint de admin (incluindo planos, shipping, etc.)
      const response = await fetch(`${API_URL}/api/admin/products/${productId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Erro desconhecido' }));
        console.error('❌ Erro na resposta:', errorData);
        throw new Error(errorData.message || `Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('✅ Dados recebidos:', data);
      
      if (data.success) {
        setProductDetails(data.data);
      } else {
        throw new Error(data.message || 'Erro ao carregar detalhes do produto');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar detalhes do produto:', err);
      setError(err.message || 'Erro ao carregar detalhes do produto');
    } finally {
      setLoadingDetails(false);
    }
  };

  // Abrir Drawer de detalhes
  const handleViewProduct = async (product) => {
    setSelectedProduct(product);
    setShowDetailsDrawer(true);
    await loadProductDetails(product.id);
  };

  // Aprovar produto
  const handleApprove = async () => {
    if (!selectedProduct) return;

    try {
      setActionLoading(true);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${API_URL}/api/admin/products/${selectedProduct.id}/approve`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          notes: approvalNotes.trim() || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao aprovar produto');
      }

      const data = await response.json();

      if (data.success) {
        setShowApprovalModal(false);
        setShowDetailsDrawer(false);
        setSelectedProduct(null);
        setProductDetails(null);
        setApprovalNotes('');
        
        loadProducts();
        loadStats();
        
        alert('✅ Produto aprovado com sucesso!');
      } else {
        throw new Error(data.message || 'Erro ao aprovar produto');
      }
      
    } catch (err) {
      alert('❌ Erro ao aprovar produto: ' + err.message);
      console.error('Erro ao aprovar produto:', err);
    } finally {
      setActionLoading(false);
    }
  };

  // Rejeitar produto
  const handleReject = async () => {
    if (!selectedProduct || !rejectionReason.trim()) {
      alert('Por favor, informe o motivo da rejeição.');
      return;
    }

    try {
      setActionLoading(true);
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${API_URL}/api/admin/products/${selectedProduct.id}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: rejectionReason.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Erro ao rejeitar produto');
      }

      const data = await response.json();

      if (data.success) {
        setShowRejectionModal(false);
        setShowDetailsDrawer(false);
        setSelectedProduct(null);
        setProductDetails(null);
        setRejectionReason('');
        
        loadProducts();
        loadStats();
        
        alert('❌ Produto rejeitado com sucesso!');
      } else {
        throw new Error(data.message || 'Erro ao rejeitar produto');
      }
      
    } catch (err) {
      alert('❌ Erro ao rejeitar produto: ' + err.message);
      console.error('Erro ao rejeitar produto:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      date_from: '',
      date_to: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewChange = (view) => {
    setActiveView(view);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Preparar estatísticas para as abas
  const tabStats = useMemo(() => {
    if (!stats) return { pending: 0, approved: 0, rejected: 0 };
    return {
      pending: stats.pending || 0,
      approved: stats.approved || 0,
      rejected: stats.rejected || 0
    };
  }, [stats]);

  // Filtrar products por busca
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return products;
    const term = searchTerm.toLowerCase();
    return products.filter(p => 
      (p.name && p.name.toLowerCase().includes(term)) ||
      (p.product_code && p.product_code.toLowerCase().includes(term)) ||
      (p.producer_name && p.producer_name.toLowerCase().includes(term)) ||
      (p.producer_email && p.producer_email.toLowerCase().includes(term))
    );
  }, [products, searchTerm]);

  return (
    <MasterLayout>
      <div className="container-fluid py-4">
        <AdminPageHeader
          title="Aprovação de Produtos"
          subtitle="Gerencie produtos pendentes de aprovação"
          icon="mdi:package-check"
        />

        <div style={{ marginBottom: '24px' }}></div>

        {/* Estatísticas - StatCard padronizado */}
        {stats && (
          <Row style={{ marginBottom: '24px' }}>
            <Col md={3}>
              <StatCard
                title="Pendentes"
                value={stats.pending}
                icon="mdi:clock-outline"
                variant="orange"
                subtitle={`${stats.pendingToday} hoje`}
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Aprovados"
                value={stats.approved}
                icon="mdi:check-circle"
                variant="green"
                subtitle={`${stats.approvedToday} hoje`}
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Rejeitados"
                value={stats.rejected}
                icon="mdi:close-circle"
                variant="purple"
              />
            </Col>
            <Col md={3}>
              <StatCard
                title="Pendentes (Semana)"
                value={stats.pendingThisWeek}
                icon="mdi:calendar-week"
                variant="blue"
                subtitle={`${stats.approvedThisWeek} aprovados`}
              />
            </Col>
          </Row>
        )}

        {/* Card principal */}
        <Card className="bg-transparent border-0">
          <Card.Header className="d-none"></Card.Header>
          <Card.Body>
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')} style={{ marginBottom: '24px' }}>
                <Icon icon="mdi:alert-circle" /> {error}
              </Alert>
            )}

            {/* Botões de Filtro (Abas) - Padrão Tickets */}
            <div className="d-flex gap-2 flex-wrap" style={{ marginBottom: '24px', gap: '8px' }}>
              <Button
                variant={activeView === 'pending' ? 'warning' : 'outline-warning'}
                onClick={() => handleViewChange('pending')}
                className="d-flex align-items-center gap-2"
              >
                <Icon icon="mdi:clock-outline" />
                Pendentes
                {tabStats.pending > 0 && (
                  <Badge bg="light" text="dark">{tabStats.pending}</Badge>
                )}
              </Button>
              
              <Button
                variant={activeView === 'approved' ? 'success' : 'outline-success'}
                onClick={() => handleViewChange('approved')}
                className="d-flex align-items-center gap-2"
              >
                <Icon icon="mdi:check-circle" />
                Aprovados
                {tabStats.approved > 0 && (
                  <Badge bg="light" text="dark">{tabStats.approved}</Badge>
                )}
              </Button>
              
              <Button
                variant={activeView === 'rejected' ? 'danger' : 'outline-danger'}
                onClick={() => handleViewChange('rejected')}
                className="d-flex align-items-center gap-2"
              >
                <Icon icon="mdi:close-circle" />
                Rejeitados
                {tabStats.rejected > 0 && (
                  <Badge bg="light" text="dark">{tabStats.rejected}</Badge>
                )}
              </Button>
            </div>

            {/* Filtros (Data) */}
            <ApprovalFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClearFilters={handleClearFilters}
              loading={loading}
            />

            {/* Busca - Separada como na tela de Tickets */}
            <div style={{ marginBottom: '24px' }}>
              <InputGroup>
                <InputGroup.Text>
                  <Icon icon="mdi:magnify" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Buscar por nome do produto, código, nome ou email do produtor..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </div>

            {/* Tabela */}
            {loading ? (
              <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="text-muted mt-3">Carregando produtos...</p>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-5">
                <Icon 
                  icon="mdi:package-variant-closed" 
                  size={64}
                  className="text-muted mb-3"
                />
                <h6 className="text-muted">
                  {activeView === 'pending' && 'Nenhum produto pendente encontrado'}
                  {activeView === 'approved' && 'Nenhum produto aprovado encontrado'}
                  {activeView === 'rejected' && 'Nenhum produto rejeitado encontrado'}
                </h6>
              </div>
            ) : (
              <>
                <div className="table-responsive">
                  <Table hover>
                    <thead>
                      <tr>
                        <th>Produto</th>
                        <th>Produtor</th>
                        <th>Categoria</th>
                        <th>Planos</th>
                        <th>Preço Mín.</th>
                        <th>Data de Criação</th>
                        <th className="text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <tr key={product.id}>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              {product.image_url ? (
                                <img 
                                  src={product.image_url} 
                                  alt={product.name}
                                  style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '8px' }}
                                />
                              ) : (
                                <div 
                                  style={{ 
                                    width: '40px', 
                                    height: '40px', 
                                    backgroundColor: '#e5e7eb', 
                                    borderRadius: '8px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Icon icon="mdi:package-variant" style={{ fontSize: '20px', color: '#9ca3af' }} />
                                </div>
                              )}
                              <div>
                                <div className="fw-semibold">{product.name}</div>
                                <small className="text-muted">Código: {product.product_code || 'N/A'}</small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div className="fw-semibold">{product.producer_name}</div>
                              <small className="text-muted">{product.producer_email}</small>
                            </div>
                          </td>
                          <td>{product.category || 'N/A'}</td>
                          <td>
                            <Badge bg="info">
                              {product.plans_count || 0} {product.plans_count === 1 ? 'plano' : 'planos'}
                            </Badge>
                          </td>
                          <td>
                            {product.min_price ? CurrencyFormatter.centsToFormatted(product.min_price) : 'N/A'}
                          </td>
                          <td>
                            <small className="text-muted">{formatDate(product.created_at)}</small>
                          </td>
                          <td className="text-center">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleViewProduct(product)}
                              title="Ver detalhes"
                            >
                              <Icon icon="mdi:eye" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                {/* Paginação */}
                {pagination.totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <div style={{ fontSize: '14px', color: '#6c757d' }}>
                      Mostrando {((pagination.page - 1) * pagination.limit) + 1} a {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total} produtos
                    </div>
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={!pagination.hasPrev}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                      >
                        <Icon icon="mdi:chevron-left" />
                      </Button>
                      <span style={{ fontSize: '14px', padding: '6px 12px' }}>
                        {pagination.page} de {pagination.totalPages}
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        disabled={!pagination.hasNext}
                        onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                      >
                        <Icon icon="mdi:chevron-right" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </Card.Body>
        </Card>

        {/* Drawer de Detalhes */}
        <Drawer
          show={showDetailsDrawer}
          onHide={() => {
            setShowDetailsDrawer(false);
            setSelectedProduct(null);
            setProductDetails(null);
          }}
          title="Detalhes do Produto"
          icon="mdi:package-variant"
          size="xl"
          footer={
            selectedProduct && (
              <div className="d-flex gap-2 justify-content-end w-100">
                <Drawer.Button
                  variant="secondary"
                  onClick={() => {
                    setShowDetailsDrawer(false);
                    setSelectedProduct(null);
                    setProductDetails(null);
                  }}
                >
                  Fechar
                </Drawer.Button>
                
                {/* Botão Reprovar */}
                {(selectedProduct.approval_status === 'pending_approval' || selectedProduct.approval_status === 'approved') && (
                  <Drawer.Button
                    variant="danger"
                    onClick={() => {
                      setRejectionReason('');
                      setShowRejectionModal(true);
                      setShowDetailsDrawer(false);
                    }}
                    icon="mdi:close-circle"
                  >
                    Reprovar
                  </Drawer.Button>
                )}
                
                {/* Botão Aprovar */}
                {(selectedProduct.approval_status === 'pending_approval' || selectedProduct.approval_status === 'rejected') && (
                  <Drawer.Button
                    variant="success"
                    onClick={() => {
                      setApprovalNotes('');
                      setShowApprovalModal(true);
                      setShowDetailsDrawer(false);
                    }}
                    icon="mdi:check-circle"
                  >
                    Aprovar
                  </Drawer.Button>
                )}
              </div>
            )
          }
        >
          {loadingDetails ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3 text-muted">Carregando detalhes...</p>
            </div>
          ) : selectedProduct && productDetails ? (
            <>
              {/* Status e Imagem */}
              <Drawer.Section>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <Drawer.Badge
                    variant={
                      selectedProduct.approval_status === 'pending_approval' ? 'warning' :
                      selectedProduct.approval_status === 'approved' ? 'success' : 'danger'
                    }
                  >
                    {selectedProduct.approval_status === 'pending_approval' ? 'Pendente' :
                     selectedProduct.approval_status === 'approved' ? 'Aprovado' : 'Rejeitado'}
                  </Drawer.Badge>
                  <span style={{ fontSize: '12px', color: '#6c757d' }}>
                    Criado em {formatDate(selectedProduct.created_at)}
                  </span>
                </div>
                {productDetails.image && (
                  <div className="text-center mb-3">
                    <img 
                      src={productDetails.image} 
                      alt={productDetails.name}
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: '300px', 
                        borderRadius: '8px',
                        objectFit: 'contain'
                      }}
                    />
                  </div>
                )}
              </Drawer.Section>
              <Drawer.Divider />

              {/* Dados Gerais */}
              <Drawer.Section title="Dados Gerais" icon="mdi:information">
                <Drawer.Row label="Nome" value={productDetails.name} />
                <Drawer.Row label="Código" value={productDetails.product_code || productDetails.uuid || 'N/A'} />
                <Drawer.Row label="Categoria" value={productDetails.category || 'N/A'} />
                <Drawer.Row label="Status" value={productDetails.status || 'N/A'} />
              </Drawer.Section>
              <Drawer.Divider />

              {/* Descrição (com scroll) - Renderizando HTML */}
              {productDetails.description && (
                <>
                  <Drawer.Section title="Descrição" icon="mdi:text">
                    <div 
                      className={styles.descriptionBox}
                      dangerouslySetInnerHTML={{ __html: productDetails.description }}
                    />
                  </Drawer.Section>
                  <Drawer.Divider />
                </>
              )}

              {/* Dados do Produtor */}
              <Drawer.Section title="Produtor" icon="mdi:account">
                <Drawer.Row label="Nome" value={productDetails.producer?.name || selectedProduct.producer_name || 'N/A'} />
                <Drawer.Row label="Email" value={productDetails.producer?.email || selectedProduct.producer_email || 'N/A'} />
              </Drawer.Section>
              <Drawer.Divider />

              {/* Frete e Dimensões */}
              {productDetails.shipping && (
                <>
                  <Drawer.Section title="Frete e Dimensões" icon="mdi:truck-delivery">
                    <Drawer.Row label="CEP de Origem" value={productDetails.shipping.origin_zipcode || 'N/A'} />
                    <Drawer.Row label="Altura (cm)" value={productDetails.shipping.package_height || 'N/A'} />
                    <Drawer.Row label="Largura (cm)" value={productDetails.shipping.package_width || 'N/A'} />
                    <Drawer.Row label="Comprimento (cm)" value={productDetails.shipping.package_length || 'N/A'} />
                    <Drawer.Row label="Peso (kg)" value={productDetails.shipping.package_weight || 'N/A'} />
                    <Drawer.Row label="Tipo de Frete" value={productDetails.shipping.shipping_type || 'N/A'} />
                    {productDetails.shipping.free_shipping_limit && (
                      <Drawer.Row label="Frete Grátis a partir de" value={CurrencyFormatter.centsToFormatted(productDetails.shipping.free_shipping_limit)} />
                    )}
                  </Drawer.Section>
                  <Drawer.Divider />
                </>
              )}

              {/* Planos */}
              {productDetails.plans && productDetails.plans.length > 0 && (
                <>
                  <Drawer.Section title={`Planos (${productDetails.plans.length})`} icon="mdi:package-variant">
                    <div>
                      {productDetails.plans.map((plan, index) => (
                        <div key={plan.id || index} className={styles.planBox}>
                          <div className={styles.planTitle}>
                            {plan.name || `Plano ${index + 1}`}
                          </div>
                          <div className={styles.planDetails}>
                            <div>Preço: {CurrencyFormatter.centsToFormatted(plan.price)}</div>
                            {plan.description && <div>Descrição: {plan.description}</div>}
                            <div>Status: <Badge bg={plan.status === 'active' ? 'success' : 'secondary'}>{plan.status}</Badge></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Drawer.Section>
                  <Drawer.Divider />
                </>
              )}

              {/* URLs */}
              {(productDetails.sales_page_url || productDetails.thank_you_page_url || productDetails.reclame_aqui_url) && (
                <>
                  <Drawer.Section title="URLs" icon="mdi:link">
                    {productDetails.sales_page_url && (
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ fontSize: '12px', fontWeight: '500', color: '#6c757d', marginBottom: '4px' }}>
                          Página de Vendas
                        </div>
                        <a 
                          href={productDetails.sales_page_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ fontSize: '14px', wordBreak: 'break-all', color: '#007bff' }}
                        >
                          {productDetails.sales_page_url}
                        </a>
                      </div>
                    )}
                    {productDetails.thank_you_page_url && (
                      <div style={{ marginBottom: productDetails.reclame_aqui_url ? '12px' : '0' }}>
                        <div style={{ fontSize: '12px', fontWeight: '500', color: '#6c757d', marginBottom: '4px' }}>
                          Página de Agradecimento
                        </div>
                        <a 
                          href={productDetails.thank_you_page_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ fontSize: '14px', wordBreak: 'break-all', color: '#007bff' }}
                        >
                          {productDetails.thank_you_page_url}
                        </a>
                      </div>
                    )}
                    {productDetails.reclame_aqui_url && (
                      <div>
                        <div style={{ fontSize: '12px', fontWeight: '500', color: '#6c757d', marginBottom: '4px' }}>
                          Reclame Aqui
                        </div>
                        <a 
                          href={productDetails.reclame_aqui_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ fontSize: '14px', wordBreak: 'break-all', color: '#007bff' }}
                        >
                          {productDetails.reclame_aqui_url}
                        </a>
                      </div>
                    )}
                  </Drawer.Section>
                  <Drawer.Divider />
                </>
              )}

              {/* Motivo da Rejeição */}
              {selectedProduct.approval_status === 'rejected' && selectedProduct.rejection_reason && (
                <>
                  <Drawer.Section title="Motivo da Rejeição" icon="mdi:alert-circle">
                    <p style={{ color: '#dc3545', fontSize: '14px' }}>{selectedProduct.rejection_reason}</p>
                  </Drawer.Section>
                </>
              )}
            </>
          ) : (
            <Drawer.EmptyState 
              icon="mdi:alert"
              text="Não foi possível carregar os detalhes do produto."
            />
          )}
        </Drawer>

        {/* Modal de Aprovação */}
        <Modal show={showApprovalModal} onHide={() => {
          setShowApprovalModal(false);
          setSelectedProduct(null);
          setApprovalNotes('');
        }}>
          <Modal.Header closeButton>
            <Modal.Title>
              <Icon icon="mdi:check-circle" style={{ color: '#10B981', marginRight: '8px' }} />
              Aprovar Produto
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedProduct && (
              <>
                <p><strong>Produto:</strong> {selectedProduct.name}</p>
                <p><strong>Produtor:</strong> {selectedProduct.producer_name}</p>
                <Form.Group className="mt-3">
                  <Form.Label>Observações (opcional)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Adicione observações sobre a aprovação..."
                    value={approvalNotes}
                    onChange={(e) => setApprovalNotes(e.target.value)}
                  />
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowApprovalModal(false);
                setSelectedProduct(null);
                setApprovalNotes('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="success"
              onClick={handleApprove}
              disabled={actionLoading}
            >
              {actionLoading ? (
                <>
                  <Icon icon="mdi:loading" className="spinner" /> Aprovando...
                </>
              ) : (
                <>
                  <Icon icon="mdi:check" /> Confirmar Aprovação
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Rejeição */}
        <Modal show={showRejectionModal} onHide={() => {
          setShowRejectionModal(false);
          setSelectedProduct(null);
          setRejectionReason('');
        }}>
          <Modal.Header closeButton>
            <Modal.Title>
              <Icon icon="mdi:close-circle" style={{ color: '#EF4444', marginRight: '8px' }} />
              Rejeitar Produto
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedProduct && (
              <>
                <p><strong>Produto:</strong> {selectedProduct.name}</p>
                <p><strong>Produtor:</strong> {selectedProduct.producer_name}</p>
                <Form.Group className="mt-3">
                  <Form.Label>Motivo da Rejeição <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Informe o motivo da rejeição..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    required
                  />
                  <Form.Text className="text-muted">
                    Este motivo será enviado ao produtor.
                  </Form.Text>
                </Form.Group>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowRejectionModal(false);
                setSelectedProduct(null);
                setRejectionReason('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={handleReject}
              disabled={actionLoading || !rejectionReason.trim()}
            >
              {actionLoading ? (
                <>
                  <Icon icon="mdi:loading" className="spinner" /> Rejeitando...
                </>
              ) : (
                <>
                  <Icon icon="mdi:close" /> Confirmar Rejeição
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </MasterLayout>
  );
};

export default AdminProductsApprovalPage;
